import type { ComponentPropsWithoutRef } from "react";
import { t } from "ttag";

import { LogoLoader } from "@/shared/components";
import { useBoolean } from "@/shared/hooks";
import {
  Button,
  CheckCircleIcon,
  DisketteIcon,
  TrashBinIcon,
} from "@/shared/ui";
import { handleAxiosFieldErrors } from "@/shared/utils";
import { useEditSessionContext } from "../context";
import { useUpdateSession } from "../hooks";
import { DeleteSessionModal } from "./delete-session-modal";
import type { ActionsVariants } from "./utils";
import { actionsVariants } from "./utils";

const DELETE_FIELDS = ["id", "enrolledUsers"] as const;

export const EditSessionActions = ({ position }: ActionsVariants) => {
  const {
    value: showDeleteModal,
    setTrue: openDeleteModal,
    setFalse: closeDeleteModal,
  } = useBoolean(false);
  const {
    form: {
      formState: { dirtyFields },
      handleSubmit,
      getValues,
      setError,
    },
  } = useEditSessionContext();

  const isDraft = !getValues("published");

  const updateSessionMutation = useUpdateSession(setError);

  const handleSave = handleSubmit((value) =>
    updateSessionMutation.mutate(value, {
      onError: (error) => {
        handleAxiosFieldErrors(error, setError);
      },
    }),
  );

  const handlePublish = handleSubmit((value) =>
    updateSessionMutation.mutate({ ...value, published: true }),
  );

  const sessionToDelete = Object.fromEntries(
    DELETE_FIELDS.map((field, index) => [
      field,
      getValues(DELETE_FIELDS)[index],
    ]),
  ) as ComponentPropsWithoutRef<typeof DeleteSessionModal>["session"];

  const isDirty = Object.keys(dirtyFields).length > 0;

  const { container, tertiaryButton } = actionsVariants({ position, isDraft });

  return (
    <div className={container()}>
      <Button
        onClick={openDeleteModal}
        variant={isDraft ? "tertiary" : "secondary"}
        color="red"
        size="lg"
        className={tertiaryButton()}
      >
        <TrashBinIcon className="size-4" />
        {t`Delete`}
      </Button>

      <Button
        onClick={handleSave}
        disabled={!isDirty}
        size="lg"
        variant={isDraft ? "secondary" : "primary"}
      >
        <DisketteIcon className="size-4" />
        {t`Save`}
      </Button>

      {isDraft && (
        <Button onClick={handlePublish} disabled={!isDirty} size="lg">
          <CheckCircleIcon className="size-4" />
          {t`Publish`}
        </Button>
      )}

      <DeleteSessionModal
        session={sessionToDelete}
        show={showDeleteModal}
        onClose={closeDeleteModal}
      />

      <LogoLoader show={updateSessionMutation.isPending} fullscreen />
    </div>
  );
};
